/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
    };
}

export interface GridStyles {
    GridItemStyle?: InjectableCss;
    GridWrapperStyle?: InjectableCss;
}

export const gridStyles: GridStyles = {
    GridWrapperStyle: {
        css: css`
            &:last-child {
                margin-bottom: 104px !important;
                margin-top: 12px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                theme,
                    [
                        css`
                        margin-bottom: 56px !important;
                        `,
                        css`
                        margin-bottom: 56px !important;
                        `,
                        css`
                        margin-bottom: 78px !important;
                        `,
                        null,
                        ],
            )}
            }
        `,
    },
    GridItemStyle: {
        css: css`
            flex: 1;
            flex-basis: 100%;
            
            > div {
                background: ${COLORS.background.grayShade};
                height: 100%;
                transition: all .2s ease-in-out;
                @media (min-width: 768px) {
                    .defaultMobileIcon, hoverMobileIcon {
                        display: none;
                    }
                    &:hover {
                        box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
                        transform: scale(1.05);
                    }
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                            transform: scale(1);
                                box-shadow: none;
                                .hoverMobileIcon {
                                    display: none;
                                }
                                .defaultMobileIcon {
                                    display: inline-flex;
                                    align-items: center;
                                }
                            &.zoom-in {
                                transform: scale(1.05);
                                box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
                                .hoverMobileIcon {
                                    display: inline-flex;
                                    align-items: center;
                                }
                                .defaultMobileIcon {
                                    display: none;
                                }
                            }
                            `,
                            css`
                            transform: scale(1);
                                box-shadow: none;
                                .hoverMobileIcon {
                                    display: none;
                                }
                                .defaultMobileIcon {
                                    display: inline-flex;
                                    align-items: center;
                                }
                            &.zoom-in {
                                transform: scale(1.05);
                                box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
                                .hoverMobileIcon {
                                    display: inline-flex;
                                    align-items: center;
                                }
                                .defaultMobileIcon {
                                    display: none;
                                }
                            }
                            `,
                            null,
                            null,
                        ],
                    );
                }}
            }
        `,
    },
};

const styles = gridStyles;

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [12, 3, 1, 1, 1],
    2: [12, 4, 2, 2, 2],
    3: [12, 6, 6, 3, 3],
    4: [12, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 12, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    const addClassMobileHover = (e: any) => {
        const mobileCloseBtn = document.querySelector(`.${e.currentTarget.classList[2]} > div`);
        mobileCloseBtn?.classList.toggle("zoom-in");
    };

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem {...styles.GridItemStyle} data-test-selector="whyCentroRow" className={`Content${x}`} onClick={(e) => addClassMobileHover(e)} key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    return <GridContainer gap={gap || 0} {...styles.GridWrapperStyle}>{columnElements}</GridContainer>;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
